// Attribution for third-party assets shipped with the game. CC-BY / MIT
// licences REQUIRE this credit to be shown to players (Settings → Credits).
// Add one entry per asset (or asset pack) when you ship it.
export interface AssetCredit {
  /** What it is in the game, e.g. "Shark". */
  what: string;
  /** Original title + author. */
  source: string;
  license: "CC0" | "CC-BY 4.0" | "CC-BY 3.0" | "MIT";
  url: string;
}

export const ASSET_CREDITS: AssetCredit[] = [
  {
    what: "Scattered rocks, boulders and bush foliage (photoscans)",
    source: "Poly Haven photoscans: rock_07, rock_09, namaqualand_boulder_05; bush leaves from shrub_03",
    license: "CC0",
    url: "https://polyhaven.com/models",
  },
  {
    what: "Character suit, glove, boot and hat surface detail (photoscans)",
    source: "Poly Haven photoscans: denim_fabric_04, fabric_leather_02, leather_white, brown_leather, caban",
    license: "CC0",
    url: "https://polyhaven.com/textures",
  },
  {
    what: "Daylight sky lighting (HDRI)",
    source: "Kloofendal 48d Partly Cloudy (Pure Sky) by Greg Zaal, Poly Haven",
    license: "CC0",
    url: "https://polyhaven.com/a/kloofendal_48d_partly_cloudy_puresky",
  },
  {
    what: "Map surface textures (walls, ground, paving, roofs, trees, rocks)",
    source: "Poly Haven photoscans: metal_plate_02, white_sandstone_bricks, concrete_wall_004, "
      + "castle_wall_varriation, painted_plaster_wall, weathered_brown_planks, concrete_floor_02, "
      + "concrete_wall_008, concrete_pavement_02, concrete_tiles_02, bark_brown_02, rock_face, "
      + "gravel_concrete, corrugated_iron, forest_leaves_02, rocky_trail, forest_ground_04",
    license: "CC0",
    url: "https://polyhaven.com",
  },
  {
    what: "Zombie body (mesh, rig, textures — repainted as a zombie)",
    source: "Microsoft Rocketbox avatar library: Male_Adult_01, © 2020 Microsoft",
    license: "MIT",
    url: "https://github.com/microsoft/Microsoft-Rocketbox",
  },
  {
    what: "Zombie animations (idle, walk, run, attack, death, hit — retargeted)",
    source: "Male City Zombie by Rikindle3D",
    license: "CC0",
    url: "https://opengameart.org/content/male-city-zombie-ready-for-use-in-game-engines",
  },
  {
    what: "Shark (mesh, rig, swim / bite / circling animations)",
    source: "shark.glb from the Babylon.js Assets library, by Babylon.js",
    license: "CC-BY 4.0",
    url: "https://github.com/BabylonJS/Assets/blob/master/meshes/shark.glb",
  },
  {
    what: "Kraken (mesh and rig; recoloured with glowing suckers, arm motion is ours)",
    source: "\"Kraken v2\" by lawtrigg (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/kraken-v2-4691fa1b881b4d43936b706f18dba169",
  },
  {
    what: "Horse (mesh, rig, saddle and gait animations; coat, mane and tack recoloured by us)",
    source: "\"Horse\" by henry_7 (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/horse-a6f860e43e364619bccb174a1ac7d0c9",
  },
  {
    what: "Camel (mesh, rig and idle/walk animations; hide, blanket, proportions and clip timing reworked, saddle and bags added by us)",
    source: "\"Camel\" by E.A.Cornell (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/camel-99af7daa5baf450ea7b809937937e6d5",
  },
  {
    what: "Ambient birds (mesh and wingbeat from a crow rig; wings, tail, head, plumage and flight cycle reworked by us)",
    source: "\"Crow\" by alexanders823 (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/crow-d5a9b0df4da3493688b63ce42c8a83e2",
  },
  {
    what: "Ambient fish (mesh and swim cycle from a bream rig; body shape, colouring and tail beat reworked by us)",
    source: "\"Bream Fish ( Dorade Royale)\" by BlueMesh (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/bream-fish-dorade-royale-a3d0e1a597794a9bb74739a13cfc8b77",
  },
  {
    what: "Ambient hares (mesh and hop cycle from a rabbit rig; ears, tail, head and coat reworked by us)",
    source: "\"Rabbit Rigged\" by FourthGreen (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/rabbit-rigged-e7213589744d436b9d96e2dbb31198a5",
  },
  {
    what: "Frontier water tower (re-proportioned, repainted; bracing + signage are ours)",
    source: "\"Water Tower Western\" by Andrew_Fox (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/water-tower-western-36b0100afc894e5283b259fc65a50d17",
  },
  {
    what: "Whiskey casks (reshaped, re-stained, our brand)",
    source: "\"Wooden Barrel (game ready asset)\" by Aparicio Silva 3D (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/wooden-barrel-game-ready-asset-327709ad4fb84207bfc38d388a946163",
  },
  {
    what: "Supply crates (re-proportioned; iron brackets + stencils are ours)",
    source: "\"Wooden Boxes\" by MaX3Dd (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/wooden-boxes-ddecbe4586594bddb4822a90c0cba222",
  },
  {
    what: "Covered wagons (repainted; canvas bonnet and bows are ours)",
    source: "\"Wagon\" by FlukierJupiter (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/wagon-f491eb62aaa24fdeb6dc52a1aab54d0c",
  },
  {
    what: "Town wells (re-proportioned, re-stained; windlass + bucket are ours)",
    source: "\"Water well\" by Ethian74 (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/water-well-c676d6de37064166871484dc54e45bca",
  },
  {
    what: "Haystacks (bales combined into our own stacked rick)",
    source: "\"Hay Bales\" by FrodoUndead (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/hay-bales-6b775049e57e4d9da87977905493ebfb",
  },
  {
    what: "European dragon (mesh, rig and flight animation; reshaped, re-horned, re-skinned and retimed by us)",
    source: "\"European Dragon\" by Regina Cachoa (Sketchfab)",
    license: "CC-BY 4.0",
    url: "https://sketchfab.com/3d-models/european-dragon-82f393a2e6c048ad80c171ce3b3a7b87",
  },
];
